import React, { useRef, useState } from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Typography,
  type SxProps,
  type Theme,
} from '@mui/material';
import { ArrowDropDown, CloudUpload, FolderOpen } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  COMPACT_UPLOAD_BUTTON_HEIGHT,
  COMPACT_UPLOAD_BUTTON_MIN_WIDTH,
  COMPACT_UPLOAD_LABELS,
} from './CompactUploadButton';

export type UploadSource = 'device' | 'temp_files';

export interface UploadSourceButtonProps {
  label?: string;
  uploading?: boolean;
  disabled?: boolean;
  allowTempFilePick?: boolean;
  onSelectSource: (source: UploadSource) => void;
  sx?: SxProps<Theme>;
}

export const UploadSourceButton: React.FC<UploadSourceButtonProps> = ({
  label = COMPACT_UPLOAD_LABELS.file,
  uploading = false,
  disabled = false,
  allowTempFilePick = false,
  onSelectSource,
  sx,
}) => {
  const theme = useTheme();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const splitRef = useRef<HTMLDivElement>(null);
  const isDisabled = disabled && !uploading;
  const menuOpen = Boolean(menuAnchor);

  const handlePrimaryClick = () => {
    if (isDisabled || uploading) return;
    if (allowTempFilePick) {
      setMenuAnchor(splitRef.current);
    } else {
      onSelectSource('device');
    }
  };

  const handleMenuChoice = (source: UploadSource) => {
    setMenuAnchor(null);
    onSelectSource(source);
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0.5,
        minWidth: COMPACT_UPLOAD_BUTTON_MIN_WIDTH,
      }}
    >
      <Box ref={splitRef} sx={{ display: 'inline-flex', alignItems: 'stretch' }}>
        <Paper
          variant="outlined"
          component="button"
          type="button"
          disabled={isDisabled}
          aria-busy={uploading}
          aria-haspopup={allowTempFilePick ? 'menu' : undefined}
          aria-expanded={allowTempFilePick ? menuOpen : undefined}
          onClick={handlePrimaryClick}
          sx={[
            {
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 0.75,
              boxSizing: 'border-box',
              minWidth: allowTempFilePick ? COMPACT_UPLOAD_BUTTON_MIN_WIDTH - 28 : COMPACT_UPLOAD_BUTTON_MIN_WIDTH,
              height: COMPACT_UPLOAD_BUTTON_HEIGHT,
              px: 1.5,
              py: 0,
              border: '1px dashed',
              borderColor: 'divider',
              borderRight: allowTempFilePick ? 'none' : undefined,
              borderTopRightRadius: allowTempFilePick ? 0 : undefined,
              borderBottomRightRadius: allowTempFilePick ? 0 : undefined,
              bgcolor: 'transparent',
              flexShrink: 0,
              cursor: isDisabled ? 'not-allowed' : uploading ? 'wait' : 'pointer',
              opacity: isDisabled ? 0.55 : 1,
              pointerEvents: uploading ? 'none' : 'auto',
            },
            uploading
              ? {
                  borderStyle: 'solid',
                  borderWidth: 2,
                  borderColor: 'primary.main',
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                }
              : {
                  '&:hover': {
                    bgcolor: isDisabled ? 'transparent' : 'action.hover',
                    borderColor: isDisabled ? 'divider' : 'primary.light',
                  },
                },
            ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
          ]}
        >
          {uploading ? (
            <CircularProgress size={18} thickness={5} color="primary" />
          ) : (
            <CloudUpload sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }} />
          )}
          <Typography
            variant="caption"
            sx={{
              fontWeight: uploading ? 700 : 500,
              lineHeight: 1,
              whiteSpace: 'nowrap',
              color: uploading ? 'primary.main' : 'text.primary',
            }}
          >
            {uploading ? COMPACT_UPLOAD_LABELS.uploading : label}
          </Typography>
        </Paper>
        {allowTempFilePick ? (
          <Paper
            variant="outlined"
            component="button"
            type="button"
            disabled={isDisabled}
            aria-label="Upload source"
            onClick={(e) => {
              if (isDisabled || uploading) return;
              setMenuAnchor(e.currentTarget);
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 28,
              height: COMPACT_UPLOAD_BUTTON_HEIGHT,
              minWidth: 28,
              p: 0,
              border: '1px dashed',
              borderColor: 'divider',
              borderLeft: 'none',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              bgcolor: 'transparent',
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              opacity: isDisabled ? 0.55 : 1,
              '&:hover': {
                bgcolor: isDisabled ? 'transparent' : 'action.hover',
                borderColor: isDisabled ? 'divider' : 'primary.light',
              },
            }}
          >
            <ArrowDropDown sx={{ fontSize: 20, color: 'text.secondary' }} />
          </Paper>
        ) : null}
      </Box>
      {uploading ? (
        <LinearProgress color="primary" sx={{ height: 3, borderRadius: 1, width: '100%' }} />
      ) : null}
      <Menu
        anchorEl={menuAnchor}
        open={menuOpen}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={() => handleMenuChoice('device')}>
          <CloudUpload fontSize="small" sx={{ mr: 1 }} />
          From this device
        </MenuItem>
        <MenuItem onClick={() => handleMenuChoice('temp_files')}>
          <FolderOpen fontSize="small" sx={{ mr: 1 }} />
          From temp files
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default UploadSourceButton;
